import { useState, useCallback, useRef } from 'react';
import { isOfferableSlotsScopeComplete, toPreferredTimeOfDay } from '@vagusx/shared';
import { getAppointmentClient } from '@/lib/api-clients';
import type { AppointmentClient, ComputedSlot } from '@vagusx/clients';
import { DEFAULT_MAX_SLOTS } from '../constants';
import type { SlotSearchParams } from '../types';

interface UseSlotSearchResult {
  slots: ComputedSlot[];
  computationMs: number;
  isSearching: boolean;
  error: string | null;
  search: (params: SlotSearchParams) => Promise<void>;
  clear: () => void;
  /** Update slots externally (e.g., from chat results) */
  updateSlots: (slots: ComputedSlot[]) => void;
  /** Re-run the last form search, if the results on screen came from one. */
  refresh: () => Promise<void>;
  /** Drop one slot from the results (it was just booked). */
  removeSlot: (slot: ComputedSlot) => void;
}

function sameSlot(a: ComputedSlot, b: ComputedSlot): boolean {
  return (
    a.startTime === b.startTime && a.providerId === b.providerId && a.locationId === b.locationId
  );
}

function fetchOfferableSlots(
  params: SlotSearchParams
): ReturnType<AppointmentClient['getOfferableSlots']> {
  // One call: the engine takes several providers AND several locations, and
  // merges across locations itself (sort, maxSlots, per-location failures).
  // An empty selection means "any", which only an operator surface may ask
  // for — hence `openScope`.
  return getAppointmentClient().getOfferableSlots({
    providerId: params.providerIds.length > 0 ? params.providerIds : undefined,
    locationId: params.locationIds.length > 0 ? params.locationIds : undefined,
    openScope: true,
    appointmentTypeId: params.appointmentTypeId!,
    fromDate: params.startDate,
    toDate: params.endDate,
    maxSlots: DEFAULT_MAX_SLOTS,
    // No per-day cap: Find Slots shows the COMPLETE set of offerable times.
    // A per-day cap keeps only the earliest N openings (applyCaps is
    // earliest-first, despite the "spread across days" intent), which
    // silently hides a busy day's afternoon. `maxSlots` still bounds payload.
    isNewPatient: params.patientType === 'new',
    sortBy: 'earliest',
    preferredTimeOfDay: toPreferredTimeOfDay(params.timeBlocks),
  });
}

/**
 * Hook to search for available appointment slots.
 *
 * Calls the unified offerable-slots engine — the single source every scheduling
 * surface shares (scraped pool → type gate → appointment-type constraints →
 * shape). Only the appointment type is required; an empty provider or location
 * selection means "any", and the engine expands it server-side.
 */
export function useSlotSearch(): UseSlotSearchResult {
  const [slots, setSlots] = useState<ComputedSlot[]>([]);
  const [computationMs, setComputationMs] = useState(0);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The search behind the results on screen; null once chat supplies them.
  const lastParams = useRef<SlotSearchParams | null>(null);
  // Bumped whenever the results are replaced, so a response that lands after a
  // newer search, chat results or a clear is dropped instead of overwriting them.
  const generation = useRef(0);

  const search = async (params: SlotSearchParams): Promise<void> => {
    if (!isOfferableSlotsScopeComplete(params)) {
      setError('Please select an appointment type');
      return;
    }
    lastParams.current = params;
    const mine = ++generation.current;

    setIsSearching(true);
    setError(null);

    try {
      const result = await fetchOfferableSlots(params);
      if (mine !== generation.current) return;
      setSlots(result.slots);
      setComputationMs(result.computationMs);
    } catch (err) {
      if (mine !== generation.current) return;
      setError(err instanceof Error ? err.message : 'Failed to search for slots');
      setSlots([]);
    } finally {
      if (mine === generation.current) setIsSearching(false);
    }
  };

  const clear = (): void => {
    generation.current++;
    setSlots([]);
    setComputationMs(0);
    setError(null);
  };

  const updateSlots = useCallback((newSlots: ComputedSlot[]): void => {
    generation.current++;
    lastParams.current = null;
    setSlots(newSlots);
    setError(null);
  }, []);

  // A quiet re-read: no loader, so the user keeps their place, and a failure
  // keeps the slots already on screen rather than blanking them.
  const refresh = async (): Promise<void> => {
    const params = lastParams.current;
    if (!params) return;
    const mine = ++generation.current;
    try {
      const result = await fetchOfferableSlots(params);
      if (mine !== generation.current) return;
      setSlots(result.slots);
      setComputationMs(result.computationMs);
    } catch {
      // Best effort: the booked slot was already dropped by `removeSlot`.
    }
  };

  const removeSlot = (slot: ComputedSlot): void => {
    setSlots((prev) => prev.filter((s) => !sameSlot(s, slot)));
  };

  return {
    slots,
    computationMs,
    isSearching,
    error,
    search,
    clear,
    updateSlots,
    refresh,
    removeSlot,
  };
}
